import {Component, Fragment, Suspense, lazy} from "react";
import {COLUMN_STATUS_PROCESSING, resolveMetricDisplayName} from "../../../columns";
import {FaIcon} from "../../../../FaIcon";
import {APP_URL} from "../../../../../settings/local_settings";
import {adgroupLevelLabel} from "../../../../../utils/channelLevelLabels";
import {toast} from "react-toastify";
// La instancia directa y no `src/i18n.js`, por lo mismo que `AlertTooltip`: importar ese módulo
// rompe las suites que mockean `react-i18next` sin el stub de `initReactI18next`.
import i18next from "i18next";

// Diferido: el modal de la regla arrastra `react-router-dom` y `SelectWithSearch`, y esta clase es la
// base de todas las celdas del Tablero. Importarlo de entrada lo cargaría en cada suite que monta una
// celda (`react-router-dom` v7 no resuelve en Jest) y en el bundle principal; así baja recién al clic.
const Ga4AttributionModal = lazy(() => import("../../../../ga4_attribution/Ga4AttributionModal"));

// Si el chunk del modal no carga (típico: una pestaña abierta antes de un deploy pide un hash que ya
// no existe), el error no puede subir al `CellWithErrorHandler`: la celda perdería su número por una
// acción secundaria. Se cierra el modal y se avisa (C-tablero-frontend-04).
class Ga4AttributionModalBoundary extends Component {
    state = {failed: false};

    static getDerivedStateFromError() {
        return {failed: true};
    }

    componentDidCatch() {
        this.props.onError();
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

const LIVE_META_STATUS_ERROR_FALLBACK = "error_fallback";
const LIVE_META_STATUS_NEVER_SYNCED = "never_synced";
const LIVE_META_STATUS_STALE = "stale";
// Los tres de arriba hablan de la frescura del sync y todos sugieren esperar. Éste dice lo
// contrario: la plataforma no publica el dato, así que no hay nada que esperar. Lo emite
// `PersistedDataCalculator.LIVE_META_STATUS_NOT_AVAILABLE`.
const LIVE_META_STATUS_NOT_AVAILABLE = "not_available_in_platform";
// `live_meta.source` del GA4 en vivo de una cuenta con filtros GA4
// (`PersistedDataCalculator.LIVE_META_SOURCE_GA4_FILTERS`).
const LIVE_META_SOURCE_GA4_FILTERS = "ga4_filters";
// `live_meta.reason` de una cuenta con filtros GA4 y sin property (`LIVE_META_REASON_MISSING_PROPERTY`).
const LIVE_META_REASON_MISSING_PROPERTY = "missing_property";
// `live_meta.reason` del «no informado» de Google en vivo: celda con valor `None` y la fila ya
// sincronizada. Lo declara el provider en vivo de Google y lo adjunta `PersistedDataCalculator`
// (donde se arma todo `live_meta`), siempre con `LIVE_META_STATUS_NOT_AVAILABLE`.
const LIVE_META_REASON_GOOGLE_SEARCH_NOT_REPORTED = "google_search_not_reported";
const LIVE_META_REASON_GOOGLE_VIDEO_NOT_REPORTED = "google_video_not_reported";
// El GA4 por grupo de anuncios de las cuentas con regla de cruce (`live_meta.source =
// 'ga4_adgroup_rule'`, contrato de la tanda «Cruce paid media ↔ GA4», § 5). Traen `channel`, para
// nombrar el nivel como lo llama la plataforma («conjunto» en Meta, «grupo» en el resto).
const LIVE_META_REASON_GA4_NO_ATTRIBUTION_RULE = "ga4_no_attribution_rule";
const LIVE_META_REASON_GA4_ADGROUP_UNMATCHED = "ga4_adgroup_unmatched";
export const LIVE_META_REASON_GA4_NOT_ADDITIVE = "ga4_not_additive";
// El motivo de `not_available_in_platform` → su texto. Es una tabla y no una cadena de `if` porque
// el `reason` lo declara cada provider del backend: un canal nuevo suma una fila acá. Un `reason`
// que no está cae al texto genérico.
const LIVE_META_REASON_COPY = {
    [LIVE_META_REASON_GOOGLE_SEARCH_NOT_REPORTED]: 'dashboard.live_meta.google_search_not_reported',
    [LIVE_META_REASON_GOOGLE_VIDEO_NOT_REPORTED]: 'dashboard.live_meta.google_video_not_reported',
    [LIVE_META_REASON_GA4_NO_ATTRIBUTION_RULE]: 'dashboard.live_meta.ga4_no_attribution_rule',
    [LIVE_META_REASON_GA4_ADGROUP_UNMATCHED]: 'dashboard.live_meta.ga4_adgroup_unmatched',
    [LIVE_META_REASON_GA4_NOT_ADDITIVE]: 'dashboard.live_meta.ga4_not_additive',
};
// En el portal del cliente (`readOnly`) el motivo se le dice al CLIENTE: «configura cómo etiquetas tus
// anuncios» le habla a la agencia. Texto neutro, sin acción (fede, 17/10).
const LIVE_META_REASON_COPY_READ_ONLY = {
    [LIVE_META_REASON_GA4_NO_ATTRIBUTION_RULE]: 'dashboard.live_meta.ga4_no_attribution_rule_portal',
};
const GA4_ADGROUP_REASONS = [
    LIVE_META_REASON_GA4_NO_ATTRIBUTION_RULE,
    LIVE_META_REASON_GA4_ADGROUP_UNMATCHED,
    LIVE_META_REASON_GA4_NOT_ADDITIVE,
];
// Alto máximo del cartel `-rich` con el texto más largo (4-5 líneas a 340px). Decide si abre hacia
// arriba, igual que el ⓘ de dato vacío de `DashboardSingleValueCell`.
const LIVE_META_TOOLTIP_HEIGHT = 170;

export class DashboardCell extends Component {
    renderLiveMetaIfNecessary() {
        const liveMeta = this._getLiveMeta();
        if (!liveMeta) {
            return null;
        }
        const status = liveMeta.status;
        if (status !== LIVE_META_STATUS_ERROR_FALLBACK
            && status !== LIVE_META_STATUS_NEVER_SYNCED
            && status !== LIVE_META_STATUS_STALE
            && status !== LIVE_META_STATUS_NOT_AVAILABLE) {
            return null;
        }
        const t = this.props.t;
        const tooltipText = this._liveMetaTooltipText(liveMeta, status, t);
        const iconName = status === LIVE_META_STATUS_ERROR_FALLBACK
            ? "exclamation-triangle"
            : "info-circle";
        let containerClass = status === LIVE_META_STATUS_ERROR_FALLBACK
            ? "warning-icon-container me-2"
            : "info-icon-container me-2";
        if (status === LIVE_META_STATUS_NOT_AVAILABLE) {
            // El ⓘ compartido va en $scale3 (2,03:1 sobre blanco). Acá es la ÚNICA puerta a la
            // explicación —la fila entera está gris y sin número—, así que necesita leerse. Se
            // sube sólo para este status, sin tocar el ícono que usan las demás celdas.
            containerClass += " -stronger";
        }
        // `-rich` para los textos largos: el de «no disponible» explica el motivo Y nombra la métrica
        // alternativa, y los de GA4 con filtros dicen qué revisar; ninguno entra en los 250px del
        // cartel default, pensado para 1-2 líneas (QA-VIS-ga4prev-09).
        const isRich = status === LIVE_META_STATUS_NOT_AVAILABLE || liveMeta.source === LIVE_META_SOURCE_GA4_FILTERS;
        const tooltipUp = Boolean((this.state || {}).liveMetaTooltipUp);
        const tooltipClass = `warning-tooltip${isRich ? " -rich" : ""}${tooltipUp ? " -up" : ""}`;
        const ga4AttributionAccountId = t ? this._ga4AttributionAccountIdOf(liveMeta) : null;
        if (ga4AttributionAccountId) {
            containerClass += " -with-cta";
        }
        // Enfocable y con su texto como etiqueta: el ⓘ de dato vacío que este cartel reemplaza en las
        // campañas ya lo era, y con teclado o lector de pantalla no había otra forma de leerlo
        // (QA-VIS-ga4prev-07).
        const indicator = (
            <div className={containerClass} key="live-meta" tabIndex={0} aria-label={tooltipText}
                 onMouseEnter={this.handleLiveMetaTooltipEnter} onFocus={this.handleLiveMetaTooltipEnter}>
                <FaIcon iconName={iconName}/>
                <div className={tooltipClass}>
                    <div className="warning-tooltip-body">
                        <p className="warning-tooltip-text">
                            {tooltipText}
                            {ga4AttributionAccountId && (
                                <button type="button" className="warning-tooltip-cta"
                                        onClick={this.handleOpenGa4AttributionModal}>
                                    {t('dashboard_cell.configure_now')}
                                </button>
                            )}
                        </p>
                    </div>
                </div>
            </div>
        );
        if (!ga4AttributionAccountId) {
            return indicator;
        }
        return (
            <Fragment key="live-meta">
                {indicator}
                {this._renderGa4AttributionModal(liveMeta)}
            </Fragment>
        );
    }

    // La cuenta cuya regla de cruce se abre desde el cartel: sólo cuando el motivo es que la cuenta no
    // tiene regla, el backend dice cuál es y quien mira puede configurarla (en el portal del cliente,
    // `readOnly`, el cartel explica pero no ofrece la acción).
    _ga4AttributionAccountIdOf(liveMeta) {
        if (liveMeta.reason !== LIVE_META_REASON_GA4_NO_ATTRIBUTION_RULE || this.props.readOnly === true) {
            return null;
        }
        return liveMeta.agency_client_ad_account_id || null;
    }

    // La regla de UNA cuenta en su propio modal (el que abren la línea y la tarjeta de la agencia): el
    // del límite diario necesita la cuenta entera, que el Tablero no tiene. Se monta sólo abierto. Al
    // guardar avisa a la fila (`onGa4AttributionSaved`), que vuelve a pedir sus datos: si no, el cartel
    // seguía invitando a configurar hasta recargar (C-tablero-frontend-02).
    _renderGa4AttributionModal(liveMeta) {
        if (!(this.state || {}).ga4AttributionModalOpen) {
            return null;
        }
        return (
            <Ga4AttributionModalBoundary onError={this.handleGa4AttributionModalLoadError}>
                <Suspense fallback={null}>
                    <Ga4AttributionModal isOpen={true}
                                         onClose={this.handleCloseGa4AttributionModal}
                                         onSaved={this.props.onGa4AttributionSaved}
                                         clientAdAccountId={liveMeta.agency_client_ad_account_id}
                                         channel={liveMeta.channel}
                                         accountName={this.props.adAccountName}/>
                </Suspense>
            </Ga4AttributionModalBoundary>
        );
    }

    handleOpenGa4AttributionModal = () => {
        this.setState({ga4AttributionModalOpen: true});
    };

    handleCloseGa4AttributionModal = () => {
        this.setState({ga4AttributionModalOpen: false});
    };

    handleGa4AttributionModalLoadError = () => {
        this.setState({ga4AttributionModalOpen: false});
        toast.error(this.props.t('ga4_attribution.agency.load_error'));
    };

    // El cartel crece hacia abajo desde la celda; en las últimas filas lo recortaba el contenedor
    // del Tablero (QA-VIS-ga4prev-01). Se mide al abrirlo, como el ⓘ de dato vacío: medir cientos de
    // celdas al montar costaría un reflow por celda para un cartel que casi nunca se abre.
    handleLiveMetaTooltipEnter = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const liveMetaTooltipUp = rect.top + LIVE_META_TOOLTIP_HEIGHT > window.innerHeight;
        if (liveMetaTooltipUp !== Boolean((this.state || {}).liveMetaTooltipUp)) {
            this.setState({liveMetaTooltipUp});
        }
    };

    _getLiveMeta() {
        if (this.props.liveMeta) return this.props.liveMeta;
        const columnData = this.props.columnData || this.props.data;
        return columnData && columnData.live_meta ? columnData.live_meta : null;
    }

    _liveMetaTooltipText(liveMeta, status, propsT) {
        const fetched = this._formatLiveMetaDatetime(liveMeta.fetched_at);
        const alternative = liveMeta.suggested_alternative
            ? this._liveMetaAlternativeLabel(liveMeta.suggested_alternative)
            : null;
        // Las celdas que no reciben `t` (las del portal, entre otras) traducen con la instancia de
        // i18next, como el aviso de presupuesto: con el español fijo de abajo, el cliente que eligió
        // inglés veía estos carteles en español (soporte wa#220).
        const t = propsT || (i18next.isInitialized ? i18next.t.bind(i18next) : null);
        if (t) {
            if (liveMeta.source === LIVE_META_SOURCE_GA4_FILTERS) {
                const ga4Text = this._ga4FiltersTooltipText(liveMeta, status, fetched, t);
                if (ga4Text) return ga4Text;
            }
            if (status === LIVE_META_STATUS_NOT_AVAILABLE) {
                const reasonText = this._liveMetaReasonText(liveMeta, t);
                if (reasonText) return reasonText;
                return alternative
                    ? t('dashboard.live_meta.not_available_with_alternative',
                        {alternative, interpolation: {escapeValue: false}})
                    : t('dashboard.live_meta.not_available');
            }
            if (status === LIVE_META_STATUS_NEVER_SYNCED) {
                return t('dashboard.live_meta.never_synced');
            }
            // `escapeValue: false`: la fecha formateada lleva «/», que i18next escaparía a `&#x2F;`
            // (C-GA4PREV-07). Es texto React, así que es seguro.
            if (status === LIVE_META_STATUS_ERROR_FALLBACK) {
                return t('dashboard.live_meta.error_fallback',
                    {fetched_at: fetched, interpolation: {escapeValue: false}});
            }
            return t('dashboard.live_meta.stale', {fetched_at: fetched, interpolation: {escapeValue: false}});
        }
        // Fallback si i18next no está inicializado (tests que no lo arrancan)
        if (status === LIVE_META_STATUS_NOT_AVAILABLE) {
            const reasonText = this._liveMetaReasonText(liveMeta, null);
            if (reasonText) return reasonText;
            return alternative
                ? `La plataforma no expone este dato. En su lugar se puede usar «${alternative}».`
                : "La plataforma no expone este dato, así que no se puede medir.";
        }
        if (status === LIVE_META_STATUS_NEVER_SYNCED) {
            return "Todavía no se trajeron datos de la plataforma para esta métrica.";
        }
        if (status === LIVE_META_STATUS_ERROR_FALLBACK) {
            return `Último dato: ${fetched}. Hubo un error al actualizar desde la plataforma.`;
        }
        return `Datos del ${fetched}. Esperando actualización.`;
    }

    // El GA4 en vivo de una cuenta con filtros: el texto genérico («de la plataforma», «en la
    // próxima sincronización») no dice que es GA4 ni que tarda minutos, y el ⓘ de «sin dato» que
    // se veía antes hacía sospechar de los filtros (Fli Fla Flu, 25/09). El «stale» sigue con el
    // texto genérico, que ya es correcto.
    _ga4FiltersTooltipText(liveMeta, status, fetched, t) {
        if (liveMeta.reason === LIVE_META_REASON_MISSING_PROPERTY) {
            return t('dashboard.live_meta.ga4_filters_missing_property');
        }
        if (status === LIVE_META_STATUS_NEVER_SYNCED) {
            return t('dashboard.live_meta.ga4_filters_never_synced');
        }
        if (status === LIVE_META_STATUS_ERROR_FALLBACK) {
            // `escapeValue: false`: la fecha formateada lleva «/», que i18next escaparía a
            // `&#x2F;` (C-GA4PREV-07). Es texto React, así que es seguro.
            return liveMeta.fetched_at
                ? t('dashboard.live_meta.ga4_filters_error_fallback',
                    {fetched_at: fetched, interpolation: {escapeValue: false}})
                : t('dashboard.live_meta.ga4_filters_error_no_data');
        }
        return null;
    }

    // El texto del motivo, o `null` si el `reason` no está en la tabla. `hasOwnProperty` y no
    // `in`: un `reason` como «toString» encontraría el del prototipo. Sin `t`, con la instancia de
    // i18next, como el aviso de presupuesto; sin i18next inicializado, el genérico.
    _liveMetaReasonText(liveMeta, t) {
        const reason = liveMeta.reason;
        if (!Object.prototype.hasOwnProperty.call(LIVE_META_REASON_COPY, reason)) return null;
        const readOnlyKey = this.props.readOnly === true
            && Object.prototype.hasOwnProperty.call(LIVE_META_REASON_COPY_READ_ONLY, reason);
        const key = readOnlyKey ? LIVE_META_REASON_COPY_READ_ONLY[reason] : LIVE_META_REASON_COPY[reason];
        const translate = t || (i18next.isInitialized ? i18next.t.bind(i18next) : null);
        if (!translate) return null;
        if (!GA4_ADGROUP_REASONS.includes(reason)) return translate(key);
        // `escapeValue: false`: el rótulo va a texto React, que ya escapa.
        return translate(key, {
            level: adgroupLevelLabel(translate, liveMeta.channel),
            levelPlural: adgroupLevelLabel(translate, liveMeta.channel, {count: 2}),
            interpolation: {escapeValue: false},
        });
    }

    _liveMetaAlternativeLabel(metricName) {
        // El backend manda el identificador; el nombre que lee el usuario lo resuelve el catálogo
        // del frontend, igual que en el resto del Tablero.
        return resolveMetricDisplayName(metricName);
    }

    _formatLiveMetaDatetime(isoString) {
        if (!isoString) return "—";
        try {
            const d = new Date(isoString);
            // 24 h: con `es-AR` el default es 12 h («08:01 p. m.») y el producto usa 24 h (QA-VIS-ga4prev-09).
            return d.toLocaleString('es-AR', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'});
        } catch (e) {
            return isoString;
        }
    }

    renderWarningIfNecessary() {
        // Mismo criterio que el aviso de presupuesto de abajo: con `t` si la celda lo recibe, si no con
        // la instancia de i18next, y el español fijo sólo para un i18next sin inicializar.
        const tr = (key, fallback) => {
            if (this.props.t) return this.props.t(key);
            return i18next.isInitialized ? i18next.t(key) : fallback;
        };

        if ((this.props.data !== undefined && this.props.data.has_meta_actions_and_are_not_set) || this.props.hasMetaActionsAndAreNotSet) {
            const buId = this.props.businessUnitId;
            const baseUrl = APP_URL && APP_URL.endsWith('/') ? APP_URL.slice(0, -1) : APP_URL;
            const href = buId && baseUrl ? `${baseUrl}/business-unit/${buId}/accounts` : null;
            // El texto es nuestro (sin valores interpolados): el HTML sólo lleva el <strong>.
            const warningHtml = tr('dashboard_cell.meta_actions_not_set',
                'Para poder monitorear correctamente las conversiones y su CPA <strong>deberás seleccionar al menos una acción de conversión</strong>.');
            return (
                <div className="warning-icon-container me-2">
                    <FaIcon iconName="exclamation-triangle"/>
                    <div className="warning-tooltip">
                        <div className="warning-tooltip-body">
                            <p className="warning-tooltip-text">
                                <span dangerouslySetInnerHTML={{__html: warningHtml}}/>
                                {href ? <a className="warning-tooltip-cta" href={href} target="_blank" rel="noopener noreferrer">{tr('dashboard_cell.configure_now', 'Configurar ahora')}</a> : null}
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        if (this.props.budgetIsNotSet === true || (this.props.data && this.props.data.budget_is_not_set)) {
            const buId = this.props.businessUnitId;
            const baseUrl = APP_URL && APP_URL.endsWith('/') ? APP_URL.slice(0, -1) : APP_URL;
            const href = buId && baseUrl ? `${baseUrl}/business-unit/${buId}/` : null;
            // La celda que más muestra este aviso es la columna Presupuesto (`DashboardBudgetCell`),
            // que NO recibe `t` (C-SEGFE-02 del review): sin él se traduce con la instancia de
            // i18next, la misma que `index.js` inicializó (el `tr` de arriba). El español fijo
            // queda sólo para un i18next sin inicializar (tests que no lo arrancan).
            return (
                <div className="warning-icon-container me-2">
                    <FaIcon iconName="exclamation-triangle"/>
                    <div className="warning-tooltip">
                        <div className="warning-tooltip-body">
                            <p className="warning-tooltip-text">{tr('dashboard_cell.budget_not_set',
                                'Carga un presupuesto en esta línea de seguimiento para que Dashbo pueda seguir su gasto.')}
                                {href ? <a className="warning-tooltip-cta" href={href} target="_blank" rel="noopener noreferrer">{tr('dashboard_cell.configure_now', 'Configurar ahora')}</a> : null}</p>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    }

    processingCssClasses() {
        // Si viene con props.data
        if(this.props.data !== undefined && this.props.data.status !== undefined && this.props.data.status === COLUMN_STATUS_PROCESSING) {
            return "processing"
        }

        // Si viene con props.statuss
        if(this.props.status !== undefined && this.props.status === COLUMN_STATUS_PROCESSING) {
            return "processing"
        }

        return "";
    }
}
